import type { Chord } from '../theory/chord';
import { chordsEqual, simplify, toPitchClass, type Complexity } from '../theory/chord';
import { argmax, mod } from '../util/math';
import type { ChordSegment } from './types';

/**
 * Merges consecutive beats with the same chord into segments. `bounds` has one more entry than
 * `chords`. With `bass` (low-register chroma per beat), each segment records its first bass note.
 */
export function buildSegments(
  chords: (Chord | null)[],
  confidence: number[],
  bounds: number[],
  bass?: Float32Array[],
): ChordSegment[] {
  const segments: ChordSegment[] = [];
  let sum = 0;
  for (let i = 0; i < chords.length; i++) {
    const last = segments.at(-1);
    if (last && chordsEqual(chords[i]!, chords[i - 1]!)) {
      last.end = bounds[i + 1]!;
      last.beatCount++;
      sum += confidence[i]!;
      last.confidence = sum / last.beatCount;
    } else {
      sum = confidence[i]!;
      const segment: ChordSegment = {
        start: bounds[i]!,
        end: bounds[i + 1]!,
        startBeat: i,
        beatCount: 1,
        chord: chords[i]!,
        confidence: sum,
      };
      const low = bass?.[i];
      if (low && Math.max(...low) > 0) segment.bass = toPitchClass(argmax(low));
      segments.push(segment);
    }
  }
  return segments;
}

/**
 * Moves early changes onto the bar line. A chord that starts in the second half of a bar and
 * carries on past the next downbeat starts at that downbeat instead, the beats before going to
 * the chord before, when:
 * - it starts on the last beat (a lead-in note or a pushed strum), or
 * - with `bass` (low-register chroma per beat), the bass reaches the new root only on the
 *   downbeat (a melody or passing bass that resembles the next chord).
 * Charts and players put such changes on the bar. Returns a new array.
 */
export function snapToBarLines(
  chords: (Chord | null)[],
  beatsPerBar: number,
  firstDownbeat: number,
  bass?: Float32Array[],
): (Chord | null)[] {
  const out = [...chords];
  const bassAt = (i: number) => {
    const low = bass?.[i];
    return low && Math.max(...low) > 0 ? argmax(low) : -1;
  };
  for (let i = 1; i < out.length - 1; i++) {
    const prev = out[i - 1];
    const cur = out[i];
    if (!prev || !cur || chordsEqual(prev, cur)) continue;
    const early = beatsPerBar - mod(i - firstDownbeat, beatsPerBar); // beats to the next downbeat
    const downbeat = i + early;
    if (early > beatsPerBar / 2 || downbeat >= out.length) continue;
    let held = true;
    for (let j = i + 1; j <= downbeat; j++) held &&= chordsEqual(cur, out[j]!);
    if (!held) continue;
    let bassLate = bassAt(downbeat) === cur.root;
    for (let j = i; j < downbeat; j++) bassLate &&= bassAt(j) !== cur.root;
    if (early > 1 && !bassLate) continue;
    for (let j = i; j < downbeat; j++) out[j] = prev;
    i = downbeat;
  }
  return out;
}

/** Semitone step from one pitch class to another, −6…+5. */
const step = (from: number, to: number) => mod(to - from + 6, 12) - 6;

/**
 * A passing chord: at most a bar long, between two chords, over a bass note that isn't its own
 * root and steps (one or two semitones) from the chord before to the chord after in one
 * direction, like the B in C → G/B → Am. Players reading a simple chart play straight through
 * it. A chord on its own root (the C in Bm → C → D) is a real change and stays.
 */
export function isPassingChord(
  prev: ChordSegment | undefined,
  seg: ChordSegment,
  next: ChordSegment | undefined,
  beatsPerBar: number,
): boolean {
  if (!prev?.chord || !seg.chord || !next?.chord || seg.bass === undefined) return false;
  if (seg.beatCount > beatsPerBar || seg.bass === seg.chord.root) return false;
  const into = step(prev.chord.root, seg.bass);
  const out = step(seg.bass, next.chord.root);
  const isStep = (n: number) => Math.abs(n) === 1 || Math.abs(n) === 2;
  return isStep(into) && isStep(out) && Math.sign(into) === Math.sign(out);
}

/**
 * Segments as shown at a complexity level (F2.11): in simple mode passing chords are folded into
 * the chord before; then each chord is simplified, and neighbours that became equal are merged
 * (C7 → C next to C gives one C). Confidence is the beat-weighted mean.
 */
export function simplifySegments(
  segments: ChordSegment[],
  complexity: Complexity,
  key?: { tonic: number; mode: 'major' | 'minor' },
  beatsPerBar = 4,
): ChordSegment[] {
  if (complexity === 'advanced') return segments;
  const out: ChordSegment[] = [];
  const extend = (last: ChordSegment, seg: ChordSegment) => {
    const beats = last.beatCount + seg.beatCount;
    last.confidence = (last.confidence * last.beatCount + seg.confidence * seg.beatCount) / beats;
    last.beatCount = beats;
    last.end = seg.end;
  };
  segments.forEach((seg, i) => {
    const last = out.at(-1);
    // Simple charts play straight through passing chords: the chord before simply lasts longer.
    if (
      last &&
      complexity === 'simple' &&
      isPassingChord(segments[i - 1], seg, segments[i + 1], beatsPerBar)
    ) {
      extend(last, seg);
      return;
    }
    const chord = seg.chord && simplify(seg.chord, complexity, key);
    if (last && chordsEqual(last.chord, chord)) extend(last, seg);
    else out.push({ ...seg, chord });
  });
  return out;
}

/** Index of the first segment that starts after `beat` (−1 if none): the next chord change. */
export function nextSegmentAfter(segments: ChordSegment[], beat: number): number {
  let lo = 0;
  let hi = segments.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((segments[mid]?.startBeat ?? Infinity) <= beat) lo = mid + 1;
    else hi = mid;
  }
  return lo < segments.length ? lo : -1;
}

/**
 * The upcoming change to warn about (F4.7): the next segment, if it has a chord and starts
 * within `warnBeats` beats of `beat`.
 */
export function upcomingChange(
  segments: ChordSegment[],
  beat: number,
  warnBeats: number,
): ChordSegment | null {
  if (warnBeats <= 0 || beat < 0) return null;
  const next = segments[nextSegmentAfter(segments, beat)];
  if (!next?.chord) return null;
  const left = next.startBeat - beat;
  return left > 0 && left <= warnBeats ? next : null;
}
